/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class DataSourceAPI
 * @hideconstructor
 *
 * @classdesc implementation of the DataSourceAPI interface.
 */
define([], () => {
	class DataSourceAPI {

		static get STATE() {
			return {
				LOADING: 'loading',
				READY: 'ready',
				ERROR: 'error'
			};
		}

		/**
		 * @function DataSourceAPI#destroy
		 * @description Used to destroy the SourceModelAPI object
		 */
		destroy() { }

		/**
		 * @function DataSourceAPI#getId
		 * @description Get the ID used to reference this data source internally.
		 * @public
		 *
		 * @return {String} The ID to use internally to reference this data source
		 */
		getId() { }

		/**
		 * @function DataSourceAPI#getAssetId
		 * @description Get the asset ID (Content Store ID) for the data source.
		 * @public
		 *
		 * @return {String} Content store ID of the data source
		 */
		getAssetId() { }

		/**
		 * @function DataSourceAPI#getType
		 * @description Get the type of the data source.
		 * @public
		 *
		 * @return {String} The type of the data source (uploadedFile, dataSet2, package, ...)
		 */
		getType() { }

		/**
		 * @public
		 * @function DataSourceAPI.getProtocol
		 * @description Get the the protocol of the datasource which determines the platform of the data source and query engine
		 * @return {String} The protocol of the datasource
		 */
		getProtocol() { }

		/**
		 * @function DataSourceAPI#getLocalizedName
		 * @description Get the localized name of the data source.
		 * @public
		 *
		 * @return {String} the localized name of the data source
		 * @deprecated Should use the synchronous call {@link DataSourceAPI#getName}
		 */
		getLocalizedName() { }

		/**
		 * @function DataSourceAPI#getSearchPath
		 * @description Get the search path of the data source.
		 * @public
		 *
		 * @return {String} the search path of the data source
		 */
		getSearchPath() { }

		/**
		 * @function DataSourceAPI#getName
		 * @description Get the localized name of the data source.
		 * @public
		 *
		 * @return {String} the localized name of the data source
		 * @todo Needs to be implemented synchronously
		 */
		getName() { }

		/**
		 * @function DataSourceAPI#getState
		 * @description Get the state of the data source.
		 * @public
		 *
		 * @return {String} 'ready', 'error' otherwise 'loaded'
		 */
		getState() {}

		/**
		 * @function DataSourceAPI#loadMetadata
		 * @description Makes the necessary server calls to make metadata available on the client
		 * @return {Promise} Promise that will resolve once the data source has been loaded
		 */
		loadMetadata() { }

		/**
		 * @function DataSourceAPI#getMetadataColumn
		 * @description Returns a {@link MetadataColumnAPI} object.  Note, you must call {@link DataSourceAPI#loadMetadata} before making this call.
		 * @public
		 *
		 * @param {String} columnId The columnId to get the metadata column
		 *
		 * @return {MetadataColumnAPI} Returns a MetadataColumnAPI object.  Throws an exception if called before {@link DataSourceAPI#loadMetadata} is called
		 */
		getMetadataColumn() {}

		/**
		 * @function DataSourceAPI#relink
		 * @description relink to new datasource.
		 * @public
		 * @async
		 *
		 * @param {DataSourceInfo} newSourceInfo Information (assetId, name, type, searchPath, ..) of the new source
		 *
		 * @return {Promise} Promise that resolved once the dataSource has been relinked and the dashboard has been reloaded.
		 */
		relink() {}

		/**
		 * @function DataSourceAPI#registerUsage
		 * @description Widgets should call this method registering themselves to the dataSource.  This is to enforce.
		 * that a dataSource that's still in use cannot be deleted.
		 * @public
		 *
		 * @param {String} widgetId The id of the widget which will be using this module. Used to keep track of usage.
		 */
		registerUsage() {}

		/**
		 * @function DataSourceAPI#deregisterUsage
		 * @description Should be called when a widget is deleted or no longer references a dataSource.
		 * @public
		 *
		 * @param {String} widgetId The id of the widget which will be using this module. Used to keep track of usage.
		 */
		deregisterUsage() {}

		/**
		 * @function DataSourceAPI#isDataSourceUsed
		 * @description Check to see if the dataSource is referenced by a widget in the dashboard.
		 * @public
		 *
		 * @return {Boolean} true if the dataSource is being used by a widget otherwise false
		 */
		isDataSourceUsed() { }

		/**
		 * @function DataSourceAPI#isDataSourceUsedByWidget
		 * @description Check to see if the dataSource is being referenced by a given widget.
		 * @public
		 *
		 * @param {String} widgetId The id of the widget we want to check
		 *
		 * @return {Boolean} True if the dataSource used by widget otherwise false
		 */
		isUsedBy() { }

		/**
		 * @function DataSourceAPI#isOlapPackage
		 * @description Check to see if the current data source references an OLAP package
		 * @return {Boolen} True if the data source references an OLAP package
		 */
		isOlapPackage() { }

		/**
		 * @function DataSourceAPI#getModule
		 * @description Get the data source module
		 * @return {Object} Data module
		 */
		getModule() {}

		/**
		 * @function DataSourceAPI#getModificationTime
		 * @description Get the data source modification time
		 * @return {Promise} Promise which resolves a string whose value is the last modification time of the datasource
		 */
		getModificationTime(){}

		/**
		 * @function DataSourceAPI#hasJoinedTables
		 * @description verifies if the given table names have a joined relationship within the data source
		 * @public
		 *
		 * @param {string} tableName1
		 * @param {String} tableName2
		 *
		 * @return {Boolean} True if the table names have a joined relationship
		 */
		hasJoinedTables(/* tableName1, tableName2 */) {}

		/**
		 * @private
		 * @function DataSourceAPI#cacheJoinError
		 * @description called when a join error is encountered to cache which tables caused the error
		 * so that subsequent queries that access this combination of tables can avoid errors.
		 * NOTE: Any shaping change will result in this cache being cleared since the change could have been to resolve the join error.
		 * @param {string[]} - a list of tables that caused a join error when used in a query against this dataSource.
		 */
		cacheJoinError() {}

		/**
		 * @private
		 * @function DataSourceAPI#hasJoinError
		 * @description called to determine if a join error against the specified list of tables has occurred
		 * so that subsequent queries that access this combination of tables can avoid errors.
		 * @param {string[]} - a list of table names to validate
		 * @returns {Boolean} true if this combination of tables has thrown a join error in the past.
		 */
		hasJoinError() {}

		/**
		 * @function DataSourceAPI#getMetadataColumnList
		 * @description Returns all the metadataColumns from a dataSource that have the type specified by the selector
		 * @public
		 * @deprecated Please use findMetadataColumns
		 * @param {string} [selector] type of the metadataColumn. e.g. 'fact', 'attribute', or all columns if undefined.
		 *
		 * @return {Array} List of metadataColumn objects
		 */
		getMetadataColumnList(/* selector */) {}

		/**
		 * @function DataSourceAPI#findMetadataColumns
		 * @description Returns all the metadataColumns from a dataSource that have the name and type specified by the selector
		 * @public
		 *
		 * @param {string} searchTerm - the search term that name of metadata column contains
		 * @param {string} type - type of the metadataColumn to search. e.g. 'fact', 'attribute', or all columns if undefined.
		 *
		 * @return {Array} List of metadataColumn objects
		 */
		findMetadataColumns(/* searchTerm , type */) {}
	}

	return DataSourceAPI;
});
